import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";

// ──────────────────────────────────────────────────────────────
// DATA
// ──────────────────────────────────────────────────────────────
const COMPETITORS = [
  { name: "Charles Schwab", followers: 655700, growth: 643 },
  { name: "Jaspreet Singh", followers: 419300, growth: 80 },
  { name: "T. Rowe Price", followers: 166700, growth: 33 },
  { name: "Edward Jones", followers: 112600, growth: 467 },
  { name: "lohause", followers: 17900, growth: 39 },
  { name: "ZiVae", followers: 1300, growth: 388, us: true },
];

// Sort largest growth → smallest for the absolute bar chart
const GROWTH_DATA = [...COMPETITORS]
  .sort((a, b) => b.growth - a.growth)
  .map((c) => ({ ...c, growthLabel: c.growth.toLocaleString() }));

// Growth velocity (growth ÷ followers × 100) — the killer insight
const VELOCITY_DATA = [...COMPETITORS]
  .map((c) => ({
    name: c.name,
    velocity: parseFloat(((c.growth / c.followers) * 100).toFixed(2)),
    us: c.us,
  }))
  .sort((a, b) => b.velocity - a.velocity);

const COLORS = {
  us: "#f59e0b", // amber-500
  competitor: "#475569", // slate-600
};

// ──────────────────────────────────────────────────────────────
// CHART TOOLTIPS
// ──────────────────────────────────────────────────────────────
const GrowthTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#0a1733] border border-white/15 px-4 py-3 shadow-2xl">
      <div className="font-sans-body uppercase text-[10px] tracking-[0.24em] text-amber-400 mb-1">
        {label}
      </div>
      <div className="text-sm text-white">
        <span className="font-semibold">+{d.growth.toLocaleString()}</span>{" "}
        <span className="text-slate-400">new follows · 28 days</span>
      </div>
      <div className="text-xs text-slate-500 mt-1">
        Audience: {d.followers.toLocaleString()}
      </div>
    </div>
  );
};

const VelocityTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#0a1733] border border-white/15 px-4 py-3 shadow-2xl">
      <div className="font-sans-body uppercase text-[10px] tracking-[0.24em] text-amber-400 mb-1">
        {label}
      </div>
      <div className="text-sm text-white">
        <span className="font-semibold">{d.velocity}%</span>{" "}
        <span className="text-slate-400">audience growth in 28 days</span>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// SECTION
// ──────────────────────────────────────────────────────────────
export default function Benchmark() {
  return (
    <section
      id="benchmark"
      data-testid="benchmark-section"
      className="relative py-24 sm:py-32 overflow-hidden border-y border-white/5"
    >
      {/* Cinematic background */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1733] via-[#0d1f44]/80 to-[#0a1733]" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 h-[520px] w-[1000px] bg-amber-500/[0.05] blur-[140px] rounded-full" aria-hidden />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        {/* HEADER */}
        <div className="max-w-3xl mb-14">
          <div className="reveal flex items-center gap-3 mb-6">
            <span className="h-[1px] w-10 bg-amber-500/70" />
            <span className="font-sans-body uppercase text-[11px] tracking-[0.32em] text-amber-400">
              Attention Creates Opportunity
            </span>
          </div>
          <h2
            data-testid="benchmark-headline"
            className="reveal font-display font-light text-white text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.01em] text-balance"
            style={{ transitionDelay: "80ms" }}
          >
            Standing out matters more than{" "}
            <span className="italic text-amber-400">blending in.</span>
          </h2>
          <p
            className="reveal mt-5 text-slate-300 text-base sm:text-lg leading-relaxed"
            style={{ transitionDelay: "140ms" }}
          >
            Financial services is one of the most competitive and emotionally
            disconnected industries online. Most advisor content blends in.
            We don't. Here's what the data says when emotionally intelligent
            branding gets benchmarked against legacy firms.
          </p>
        </div>

        {/* HEADLINE INSIGHT — Growth Velocity */}
        <div className="reveal mb-10 grid grid-cols-1 md:grid-cols-12 gap-px bg-white/10">
          <div className="md:col-span-5 bg-gradient-to-br from-amber-500/[0.08] to-[#0a1733] p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-amber-500/15 rounded-full blur-3xl" aria-hidden />
            <div className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400 mb-4">
              Punching Above Our Weight
            </div>
            <div className="flex items-baseline gap-3">
              <span className="font-display text-[100px] sm:text-[140px] leading-[0.85] font-light text-amber-400">
                29.8
              </span>
              <span className="font-display text-5xl text-white">%</span>
            </div>
            <div className="mt-4 font-display text-xl text-white leading-tight">
              audience growth in 28 days
            </div>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-sm">
              Charles Schwab's velocity? <span className="text-slate-300">0.10%.</span>{" "}
              We're growing at a <span className="text-amber-300 font-semibold">~300×</span>{" "}
              rate relative to audience size — proof that emotionally
              intelligent content moves faster than legacy finance branding.
            </p>
          </div>

          {/* Velocity chart */}
          <div className="md:col-span-7 bg-[#0a1733]/80 p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400 mb-1">
                  Growth Velocity (%)
                </div>
                <div className="font-display text-xl text-white">
                  Audience expansion relative to size
                </div>
              </div>
            </div>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={VELOCITY_DATA}
                  margin={{ top: 16, right: 10, left: -10, bottom: 0 }}
                  layout="vertical"
                >
                  <XAxis
                    type="number"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#475569", fontSize: 10, fontFamily: "Outfit" }}
                    domain={[0, "dataMax + 5"]}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    width={120}
                    tick={{ fill: "#94a3b8", fontSize: 11, fontFamily: "Outfit" }}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(217,119,6,0.06)" }}
                    content={<VelocityTooltip />}
                  />
                  <Bar dataKey="velocity" radius={[0, 2, 2, 0]}>
                    {VELOCITY_DATA.map((d, i) => (
                      <Cell key={i} fill={d.us ? COLORS.us : COLORS.competitor} />
                    ))}
                    <LabelList
                      dataKey="velocity"
                      position="right"
                      formatter={(v) => `${v}%`}
                      fill="#f8fafc"
                      fontSize={11}
                      fontFamily="Outfit"
                      fontWeight={600}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ABSOLUTE GROWTH CHART */}
        <div className="reveal grid grid-cols-1 md:grid-cols-12 gap-px bg-white/10 mb-12">
          <div className="md:col-span-7 bg-[#0a1733]/80 p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400 mb-1">
                  Net New Follows · Last 28 Days
                </div>
                <div className="font-display text-xl text-white">
                  Side-by-side with the giants
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-5 text-[10px] uppercase tracking-[0.18em] text-slate-400">
                <span className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 bg-amber-500" /> ZiVae
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 bg-slate-500" /> Legacy
                </span>
              </div>
            </div>
            <div className="h-[340px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={GROWTH_DATA}
                  margin={{ top: 24, right: 10, left: -10, bottom: 40 }}
                >
                  <XAxis
                    dataKey="name"
                    axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                    tickLine={false}
                    angle={-25}
                    textAnchor="end"
                    interval={0}
                    height={50}
                    tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "Outfit" }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#475569", fontSize: 10, fontFamily: "Outfit" }}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(217,119,6,0.06)" }}
                    content={<GrowthTooltip />}
                  />
                  <Bar dataKey="growth" radius={[3, 3, 0, 0]}>
                    {GROWTH_DATA.map((d, i) => (
                      <Cell key={i} fill={d.us ? COLORS.us : COLORS.competitor} />
                    ))}
                    <LabelList
                      dataKey="growth"
                      position="top"
                      fill="#f8fafc"
                      fontSize={11}
                      fontFamily="Outfit"
                      fontWeight={600}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Insight panel */}
          <div className="md:col-span-5 bg-[#0e1e44]/70 p-8 sm:p-10 flex flex-col justify-center">
            <div className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400 mb-5">
              The Real Story
            </div>
            <p className="font-display text-2xl sm:text-3xl text-white leading-tight">
              Our content competes with brands{" "}
              <span className="italic text-amber-400">500× our size.</span>
            </p>
            <p className="mt-5 text-sm sm:text-base text-slate-300 leading-relaxed">
              Edward Jones has 112,600 followers. We have 1,300. They added
              467 new follows last month — we added 388. The gap between
              their audience and ours is enormous; the gap between our
              monthly attention is not.
            </p>
            <p className="mt-4 text-sm text-slate-400 italic">
              Different wins attention. Memorable wins trust.
            </p>
          </div>
        </div>

        {/* REAL LEAD GENERATION PERFORMANCE */}
        <div className="reveal mt-20" data-testid="lead-performance-section">
          <div className="max-w-3xl mb-10">
            <div className="font-sans-body uppercase text-[11px] tracking-[0.32em] text-amber-400 mb-3">
              Attention → Leads → Appointments → Revenue
            </div>
            <h3
              data-testid="lead-performance-headline"
              className="font-display font-light text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.01em] text-balance"
            >
              Real lead generation.{" "}
              <span className="italic text-amber-400">Real infrastructure.</span>
            </h3>
            <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed">
              Most advisors don't struggle because they lack talent. They
              struggle because inconsistent lead flow creates inconsistent
              confidence, inconsistent pipelines, and inconsistent income.
              The goal isn't survival mode — it's building systems that
              generate momentum even while you sleep.
            </p>
          </div>

          {/* Dashboard frame + image */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10">
            {/* Image panel */}
            <div className="lg:col-span-8 relative bg-[#0a1733]/80 p-5 sm:p-7">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2.5">
                  <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-emerald-400">
                    Live Campaign · Performance Overview
                  </span>
                </div>
                <span className="hidden sm:inline font-sans-body uppercase text-[10px] tracking-[0.24em] text-slate-500">
                  Meta Ads · Last 105 days
                </span>
              </div>

              <div className="relative overflow-hidden border border-white/10 bg-white">
                <img
                  src="/assets/meta-ads-performance.png"
                  alt="Meta Ads performance overview — 301 leads generated, $10.13 cost per lead, $3,048.72 ad spend"
                  className="w-full h-auto object-contain block"
                  data-testid="meta-performance-image"
                />
              </div>

              {/* Glow underlay */}
              <div className="pointer-events-none absolute -inset-3 -z-10 bg-amber-500/[0.06] blur-3xl rounded-3xl" aria-hidden />

              <p className="mt-5 text-xs text-slate-400 leading-relaxed">
                Generating leads consistently while optimizing systems,
                follow-up, and operational efficiency. Daily lead flow,
                tracked and refined.
              </p>
            </div>

            {/* Metric stack */}
            <div className="lg:col-span-4 grid grid-cols-3 lg:grid-cols-1 gap-px bg-white/10">
              <PerfMetric
                label="Leads Generated"
                value="301"
                sub="Form fills · 105 days"
                tone="amber"
              />
              <PerfMetric
                label="Cost Per Lead"
                value="$10.13"
                sub="High-intent audience"
                tone="slate"
              />
              <PerfMetric
                label="Total Ad Spend"
                value="$3,048.72"
                sub="Tracked + optimized"
                tone="emerald"
              />
            </div>
          </div>

          {/* Strategic positioning — reframing the $8.27 median */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-px bg-white/10">
            <div className="md:col-span-7 bg-[#0a1733]/80 p-7 sm:p-9">
              <div className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400 mb-4">
                Why Cheaper Isn't Better
              </div>
              <p className="font-display text-xl sm:text-2xl text-white leading-snug">
                The goal isn't just{" "}
                <span className="italic text-amber-400">cheaper leads.</span>{" "}
                It's qualified conversations, stronger attendance, and
                long-term business growth.
              </p>
              <p className="mt-5 text-sm sm:text-base text-slate-300 leading-relaxed">
                High-intent audiences — financial advisor, retirement, and
                wealth-focused — are some of the most competitive and
                expensive segments on Meta. A $2 lead from a low-intent
                audience books no appointments. A $10 lead from a
                retirement-age, asset-holding audience books revenue.
              </p>
              <p className="mt-4 text-sm text-slate-400 italic">
                Attention in financial services is competitive — but when
                messaging connects emotionally and follow-up is optimized,
                lead generation becomes scalable.
              </p>
            </div>

            <div className="md:col-span-5 bg-gradient-to-br from-amber-500/[0.06] to-[#0a1733] p-7 sm:p-9 flex flex-col justify-center">
              <div className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400 mb-5">
                The Full Operational Ecosystem
              </div>
              <ul className="space-y-3 text-sm text-slate-300">
                {[
                  "Visibility",
                  "Lead flow",
                  "Follow-up automation",
                  "Nurture sequences",
                  "Appointment attendance",
                  "Conversion + close",
                  "Long-term momentum",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <span className="h-[1px] w-5 bg-amber-500/60" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-slate-500 leading-relaxed">
                Most agencies stop at lead generation. We build the entire
                growth ecosystem behind it.
              </p>
            </div>
          </div>
        </div>

        {/* CLOSING TAKEAWAY */}
        <div className="reveal mt-16 p-8 sm:p-10 bg-gradient-to-br from-amber-500/[0.06] to-transparent border-l-2 border-amber-500 max-w-3xl mx-auto">
          <div className="font-sans-body uppercase text-[10px] tracking-[0.32em] text-amber-400 mb-3">
            The Emotional Takeaway
          </div>
          <p className="font-display text-2xl sm:text-3xl text-white leading-snug">
            "Predictable growth starts with{" "}
            <span className="italic text-amber-400">predictable lead flow.</span>{" "}
            Better systems create calmer businesses."
          </p>
          <p className="mt-5 text-sm text-slate-400">
            In crowded financial markets, attention is an asset — and
            operational infrastructure is what turns it into income.
          </p>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ──────────────────────────────────────────────────────────────
function PerfMetric({ label, value, sub, tone }) {
  const toneMap = {
    amber: "text-amber-400",
    emerald: "text-emerald-400",
    slate: "text-white",
  };
  return (
    <div className="bg-[#0a1733]/80 p-6 lg:p-7 flex flex-col justify-between min-h-[140px]">
      <div className="font-sans-body uppercase text-[10px] tracking-[0.24em] text-slate-400 leading-snug">
        {label}
      </div>
      <div className="mt-4 lg:mt-0">
        <div className={`font-display text-4xl sm:text-5xl leading-none ${toneMap[tone]}`}>
          {value}
        </div>
        {sub && (
          <div className="mt-3 text-[11px] text-slate-500 uppercase tracking-[0.18em]">
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}
